import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle, X, Lightbulb, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * Feature help content definitions
 * Each feature has a title, description, tips, and optional link to learn more
 */
export const FEATURE_HELP = {
  // Family Page Tabs
  events: {
    title: 'Family Events',
    icon: '📅',
    description: 'Plan and coordinate family gatherings, reunions, and celebrations. Track RSVPs, assign tasks, and share event details with everyone.',
    tips: [
      'Create events with clear dates and locations',
      'Use tasks to delegate responsibilities',
      'Send reminders as the event approaches',
      'Collect photos after the event'
    ],
    category: 'core'
  },
  roles: {
    title: 'Family Roles',
    icon: '👑',
    description: 'Assign leadership roles to family members. Each role has specific responsibilities that help distribute the work of managing your family group.',
    tips: [
      'Start by assigning a Treasurer and Event Coordinator',
      'Roles can be changed anytime',
      'Members can hold multiple roles',
      'Use the Role Guide to understand each role'
    ],
    learnMore: '/role-guide',
    category: 'organization'
  },
  members: {
    title: 'Family Members',
    icon: '👨‍👩‍👧‍👦',
    description: 'View and manage your family members. Invite new members, approve join requests, and keep track of everyone in your family group.',
    tips: [
      'Invite members using their email address',
      'Proxy members can be added for those without accounts',
      'Owners can transfer ownership to another member',
      'Archive inactive members instead of removing them'
    ],
    category: 'core'
  },
  chat: {
    title: 'Family Chat',
    icon: '💬',
    description: 'Real-time messaging for your family. Share updates, coordinate plans, and stay connected with everyone in one central place.',
    tips: [
      'Use chat for quick updates and discussions',
      'Everyone in the family can see messages',
      'Great for coordinating before events',
      'Keep important info in announcements instead'
    ],
    category: 'communication'
  },
  dues: {
    title: 'Family Dues',
    icon: '💰',
    description: 'Manage family contributions and finances. Set up dues schedules, track payments, and allocate funds to family events or goals.',
    tips: [
      'Set clear due dates and amounts',
      'Send reminders for overdue payments',
      'Allocate collected dues to specific events',
      'Keep records transparent for all members'
    ],
    tier: 'growing',
    category: 'finance'
  },
  governance: {
    title: 'Voting & Governance',
    icon: '🗳️',
    description: 'Make family decisions democratically. Create proposals, collect votes, and document important family decisions for future reference.',
    tips: [
      'Use for major family decisions',
      'Set clear voting deadlines',
      'Require a minimum participation threshold',
      'Document the outcome and reasoning'
    ],
    tier: 'growing',
    category: 'organization'
  },

  // Header Features
  gallery: {
    title: 'Photo Gallery',
    icon: '📸',
    description: 'Store and share family photos. Create albums, upload memories, and preserve your family\'s visual history for generations.',
    tips: [
      'Organize photos into albums by event or year',
      'Add captions to help identify people and places',
      'Download photos to keep personal copies',
      'Share albums with extended family'
    ],
    category: 'memories'
  },
  spiritual: {
    title: 'Spiritual Heritage',
    icon: '🙏',
    description: 'Honor your family\'s spiritual traditions. Share prayers, affirmations, stories of faith, and preserve your spiritual legacy.',
    tips: [
      'Add family prayers and blessings',
      'Share stories of faith from elders',
      'Document religious milestones',
      'Create a space for gratitude'
    ],
    tier: 'seedling',
    category: 'legacy'
  },
  timeline: {
    title: 'Family Timeline',
    icon: '📜',
    description: 'Document your family\'s journey through time. Mark milestones, anniversaries, and significant moments that shaped your family.',
    tips: [
      'Add major life events (births, weddings, etc.)',
      'Include historical family moments',
      'Attach photos to timeline entries',
      'Let members contribute their own memories'
    ],
    category: 'legacy'
  },
  analytics: {
    title: 'Family Analytics',
    icon: '📊',
    description: 'Gain insights into your family\'s engagement. See participation rates, event attendance, and how your family is using the platform.',
    tips: [
      'Check analytics monthly to spot trends',
      'Use insights to plan better events',
      'Identify less engaged members to reach out',
      'Celebrate milestones together'
    ],
    tier: 'flourishing',
    category: 'insights'
  },

  // Dashboard Features
  resources: {
    title: 'Professional Resources',
    icon: '📚',
    description: 'Access a curated directory of family-focused professionals. Find estate planners, counselors, financial advisors, and more.',
    tips: [
      'Browse by specialty or location',
      'Read reviews from other families',
      'Book consultations directly',
      'Share helpful resources with family'
    ],
    tier: 'flourishing',
    learnMore: '/professionals/directory',
    category: 'resources'
  },
  consultations: {
    title: 'Consultations',
    icon: '📞',
    description: 'Schedule and manage consultations with family professionals. Get expert guidance on estate planning, finances, and family matters.',
    tips: [
      'Book consultations in advance',
      'Prepare questions beforehand',
      'Take notes during sessions',
      'Follow up on action items'
    ],
    tier: 'flourishing',
    category: 'resources'
  },

  // Actions
  createFamily: {
    title: 'Create a Family',
    icon: '🏠',
    description: 'Start your family group on Legacy Forge. Invite members, set up your family profile, and begin building your legacy together.',
    tips: [
      'Choose a meaningful family name',
      'Write a description that captures your mission',
      'Invite key family members first',
      'Set up roles to share responsibilities'
    ],
    category: 'getting-started'
  },
  createEvent: {
    title: 'Create Event',
    icon: '🎉',
    description: 'Plan a new family gathering. Set the date, location, and details, then invite family members to RSVP.',
    tips: [
      'Give enough advance notice',
      'Be clear about location and time',
      'Add tasks for members to help',
      'Follow up with reminders'
    ],
    category: 'actions'
  },
  inviteMember: {
    title: 'Invite Member',
    icon: '✉️',
    description: 'Send an invitation to join your family group. They\'ll receive an email with a link to create their account and join.',
    tips: [
      'Double-check the email address',
      'Add a personal message',
      'Follow up if they don\'t respond',
      'Help them get started once they join'
    ],
    category: 'actions'
  }
};

/**
 * HelpTooltip - Rich contextual help tooltip with icons and tips.
 *
 * Renders the popup card via React Portal (mounted on document.body) and
 * positions it with `position: fixed` based on the trigger's bounding rect.
 * This avoids being clipped by any ancestor with `overflow: hidden`.
 * Also auto-flips at viewport edges so the card never goes off-screen.
 */
export function HelpTooltip({ feature, position = 'right', size = 'md' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const triggerRef = useRef(null);
  const cardRef = useRef(null);
  const help = FEATURE_HELP[feature];

  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  // Compute popup position on open + on scroll/resize while open.
  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) return;

    const computePos = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const card = cardRef.current;
      const cardWidth = card?.offsetWidth || 320;
      const cardHeight = card?.offsetHeight || 280;
      const margin = 8;
      const viewportW = window.innerWidth;
      const viewportH = window.innerHeight;

      let top, left;
      let placement = position;

      // Decide horizontal placement first based on requested side, then auto-flip if needed
      if (placement === 'right') {
        left = rect.right + margin;
        top = rect.top + rect.height / 2 - cardHeight / 2;
        if (left + cardWidth > viewportW - 8) {
          // flip to left
          left = rect.left - cardWidth - margin;
          placement = 'left';
        }
      } else if (placement === 'left') {
        left = rect.left - cardWidth - margin;
        top = rect.top + rect.height / 2 - cardHeight / 2;
        if (left < 8) {
          left = rect.right + margin;
          placement = 'right';
        }
      } else if (placement === 'top') {
        left = rect.left + rect.width / 2 - cardWidth / 2;
        top = rect.top - cardHeight - margin;
        if (top < 8) {
          top = rect.bottom + margin;
          placement = 'bottom';
        }
      } else {
        // bottom (default fallback)
        left = rect.left + rect.width / 2 - cardWidth / 2;
        top = rect.bottom + margin;
        if (top + cardHeight > viewportH - 8) {
          top = rect.top - cardHeight - margin;
          placement = 'top';
        }
      }

      // Final viewport clamps so it never goes off-screen
      left = Math.max(8, Math.min(left, viewportW - cardWidth - 8));
      top = Math.max(8, Math.min(top, viewportH - cardHeight - 8));

      setCoords({ top, left });
    };

    // Run twice: once on next frame (after first render with default size),
    // then again after the card is measured for accurate centering.
    requestAnimationFrame(computePos);
    const t = setTimeout(computePos, 0);
    window.addEventListener('scroll', computePos, true);
    window.addEventListener('resize', computePos);
    return () => {
      clearTimeout(t);
      window.removeEventListener('scroll', computePos, true);
      window.removeEventListener('resize', computePos);
    };
  }, [isOpen, position]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  if (!help) return null;

  const card = isOpen && (
    <div
      ref={cardRef}
      className="fixed z-[9999] w-72 sm:w-80 max-w-[calc(100vw-16px)]"
      style={coords ? { top: coords.top, left: coords.left } : { top: -9999, left: -9999, opacity: 0 }}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <div className="bg-slate-800 border border-slate-600 rounded-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-slate-700/50 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-2xl">{help.icon}</span>
            <h4 className="font-semibold text-white">{help.title}</h4>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="text-gray-400 hover:text-white"
            aria-label="Close help"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-3">
          <p className="text-gray-300 text-sm leading-relaxed">
            {help.description}
          </p>

          {/* Tips */}
          {help.tips && help.tips.length > 0 && (
            <div className="bg-slate-700/30 rounded-lg p-3">
              <div className="flex items-center gap-2 text-yellow-400 mb-2">
                <Lightbulb className="w-4 h-4" />
                <span className="text-xs font-semibold uppercase tracking-wide">Tips</span>
              </div>
              <ul className="space-y-1">
                {help.tips.slice(0, 3).map((tip, idx) => (
                  <li key={idx} className="text-gray-400 text-xs flex items-start gap-2">
                    <span className="text-blue-400 mt-0.5">•</span>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Tier Badge */}
          {help.tier && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Available from:</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                help.tier === 'seedling' ? 'bg-green-500/20 text-green-400' :
                help.tier === 'growing' ? 'bg-blue-500/20 text-blue-400' :
                'bg-purple-500/20 text-purple-400'
              }`}>
                {help.tier.charAt(0).toUpperCase() + help.tier.slice(1)}
              </span>
            </div>
          )}

          {/* Learn More Link */}
          {help.learnMore && (
            <Link
              to={help.learnMore}
              className="flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm mt-2"
            >
              <BookOpen className="w-4 h-4" />
              Learn more
            </Link>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <span className="inline-flex items-center">
      <button
        ref={triggerRef}
        onClick={() => setIsOpen((v) => !v)}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        className="text-gray-400 hover:text-blue-500 transition-colors focus:outline-none"
        aria-label={`Help: ${help.title}`}
        data-testid={`help-tooltip-${feature}`}
      >
        <HelpCircle className={sizeClasses[size]} />
      </button>
      {card && createPortal(card, document.body)}
    </span>
  );
}

/**
 * HelpButton - Standalone help button that links to Help Center
 */
export function HelpButton({ className = '' }) {
  return (
    <Link
      to="/help-center"
      className={`flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white hover:bg-slate-700 rounded-lg transition ${className}`}
      data-testid="help-center-btn"
    >
      <BookOpen className="w-5 h-5" />
      <span className="hidden sm:inline">Help Center</span>
    </Link>
  );
}

export default HelpTooltip;
